import { icons } from '../icons';
import React, { useState, useRef, useEffect } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { cva } from 'class-variance-authority';
import cn from '../utils/cn';
import { zIndex } from '../../z-index';
import KoobInputLabel from './KoobInputLabel';
import KoobSpinner from './KoobSpinner';
import useDismissOnOutsidePointerDown from '../hooks/useDismissOnOutsidePointerDown';
import {
  inputFrameClasses,
  inputInheritedClasses,
  inputMinSizeClasses,
  inputRoundedClasses,
  inputSizeClasses,
  inputWarningClasses,
} from './KoobInputText';
import { matchesSearchWords, toSearchWords } from '@koob/text-search';

// ponytail: options-array API kept (deviation from the composable Root+Item
// convention) — searchable/multi/creatable needs the data up front, and Radix
// has no combobox primitive. Revisit if Radix ships one.

export type KoobSelectValue = string | number;

export interface KoobSelectOption {
  value: KoobSelectValue;
  label: string;
  disabled?: boolean;
  /**
   * The domain object the option was built from, handed back untouched to
   * renderOption / renderValue. The search falls back on its title or label when
   * the option carries no plain string label.
   */
  item?: Record<string, any>;
}

type KoobSelectRenderState = { selected: boolean; focused: boolean };

const selectVariants = cva(
  'relative w-full text-left border focus:outline-none focus:ring-0 focus:ring-inset shadow-none transition-colors duration-200 overflow-hidden',
  {
    variants: {
      variant: {
        // Frame (border color + background) shared with KoobInputText so
        // selects and text fields always match in a form row.
        default: `${inputFrameClasses} text-gray-900`,
        outline: 'border-gray-200 bg-transparent text-gray-900',
        ghost: 'border-transparent bg-gray-50 text-gray-900 focus:bg-white',
      },
      // Box metrics shared with KoobInputText so selects and text fields are
      // always the same height in a form row. `multiline` (multi-select with
      // tags) swaps the fixed height for a min-height so the trigger grows
      // with the wrapped tags instead of clipping them.
      size: {
        sm: inputSizeClasses.sm,
        md: inputSizeClasses.md,
        smMultiline: inputMinSizeClasses.sm,
        mdMultiline: inputMinSizeClasses.md,
      },
      rounded: inputRoundedClasses,
      state: {
        default: '',
        disabled: 'bg-gray-100 cursor-not-allowed opacity-50',
        error: 'ring-red-500 border-red-500',
        success: 'ring-green-500',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      rounded: 'md',
      state: 'default',
    },
  },
);

const dropdownClasses =
  'overflow-hidden rounded-lg border border-solid border-gray-200 bg-white';

// Shared with KoobInputCountries so both selectors speak the same row language.
export const optionVariants = cva(
  'flex cursor-pointer select-none items-center gap-2 rounded-md transition-colors duration-150',
  {
    variants: {
      state: {
        default: 'text-gray-700 hover:bg-gray-100 hover:text-gray-900',
        focused: 'bg-gray-100 text-gray-900',
        selected: 'font-medium text-gray-900 hover:bg-gray-100',
        selectedFocused: 'bg-gray-100 font-medium text-gray-900',
        disabled: 'text-gray-400 cursor-not-allowed',
      },
      size: {
        sm: 'px-2 py-1.5 text-sm',
        md: 'px-2 py-2 text-base md:text-sm',
      },
    },
    defaultVariants: {
      state: 'default',
      size: 'md',
    },
  },
);

const searchInputSizeClasses = {
  sm: 'py-1.5 text-sm',
  md: 'py-2.5 text-base md:text-sm',
};

// Selected values of a multi-select, as removable tags inside the trigger.
// Same visual language as KoobBadge (gray/soft, rounded, xs) but kept local:
// the trigger is a <button>, so the remove control has to be a role="button"
// span (no nested <button>), which KoobBadge/KoobPill can't provide.
const tagVariants = cva(
  'inline-flex max-w-full items-center gap-1 rounded font-medium bg-gray-100 text-gray-700',
  {
    variants: {
      size: {
        sm: 'px-1.5 py-0.5 text-[11px]',
        md: 'px-2 py-0.5 text-xs',
      },
    },
    defaultVariants: { size: 'md' },
  },
);

// The trigger is `w-full`, so its width comes from the call site. Short
// labels (locale switchers, "Any"/"All" filters) used to collapse to a few
// characters and dragged the popover — which matches the trigger width —
// down with them. Call sites that really need a narrower box override with
// `style={{ minWidth: … }}` (spread onto the wrapper).
const TRIGGER_MIN_WIDTH = 'min-w-[8rem]';

type KoobInputSelectProps = {
  // not part of the API (see packages/ui/README.md); stripped below
  className?: never;
  options: KoobSelectOption[];
  value?: KoobSelectValue | KoobSelectValue[] | null;
  onValueChange?: (value: any) => void;
  /** @deprecated use onValueChange */
  setValue?: (value: any) => void;
  multiple?: boolean;
  disabled?: boolean;
  loading?: boolean;
  /** Show an ✕ on the trigger to reset to empty (null / [] for multiple). */
  clearable?: boolean;
  searchable?: boolean;
  /** Render the option list in flow under the trigger instead of a floating
   * popover (wizard-style forms where the menu shouldn't overlay the page). */
  inline?: boolean;
  /**
   * Controlled search: called as the user types in the search input, and with
   * '' when the dropdown closes. Disables internal option filtering — the
   * caller provides already-filtered `options` (e.g. async search results).
   */
  onSearchChange?: (term: string) => void;
  /** Called each time the option list shows (at mount when `inline`). */
  onOpen?: () => void;
  creatable?: boolean;
  /** Pinned action row at the bottom of the dropdown; closes the menu and
   * calls back — for create flows that open a modal. Distinct from
   * `creatable`, which turns the typed search term into the value. */
  onCreate?: () => void;
  /** Label of the `onCreate` row. Defaults to 'Create new'. */
  createLabel?: React.ReactNode;
  /** Replaces the "No options found" text — async searches use it for
   * "type to search" / "searching…" states. */
  emptyState?: React.ReactNode;
  placeholder?: string;
  label?: React.ReactNode;
  required?: boolean;
  /** Forwarded to the trigger button. */
  name?: string;
  error?: boolean;
  /** Orange border (same as KoobInputText `warning`); `error` wins. */
  warning?: boolean;
  /** Dashed border: the shown value is inherited (same as KoobInputText). */
  inherited?: boolean;
  /** @deprecated use error */
  hasError?: boolean;
  variant?: 'default' | 'outline' | 'ghost';
  /** @deprecated 'default' — use 'md' */
  size?: 'sm' | 'md' | 'default';
  rounded?: 'md' | 'full';
  /** @deprecated use error / disabled */
  state?: 'default' | 'disabled' | 'error' | 'success';
  /** Preferred side, forwarded to Radix Popover.Content. A preference only:
   * the panel still flips and shrinks to whatever the viewport leaves, so it
   * can never be pinned off-screen. There is deliberately no way to turn
   * that off — see "Overlays" in packages/ui/README.md. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  formatCreateLabel?: (input: string) => React.ReactNode;
  renderOption?: (
    option: KoobSelectOption,
    state: KoobSelectRenderState,
  ) => React.ReactNode;
  renderValue?: (option: KoobSelectOption) => React.ReactNode;
  /** @deprecated use renderValue */
  renderSelectedValue?: (option: KoobSelectOption) => React.ReactNode;
  /** @deprecated use data-testid */
  testId?: string;
  'data-testid'?: string;
};

const KoobInputSelect = React.forwardRef<HTMLDivElement, KoobInputSelectProps>(
  (
    {
      options,
      disabled = false,
      multiple = false,
      loading = false,
      clearable = false,
      value,
      onValueChange,
      setValue,
      placeholder = 'Select an option',
      label,
      required = false,
      name,
      variant = 'default',
      size = 'md',
      rounded,
      state = 'default',
      side,
      error = false,
      warning = false,
      inherited = false,
      hasError = false,
      searchable = false,
      inline = false,
      onSearchChange,
      onOpen,
      creatable = false,
      onCreate,
      createLabel,
      emptyState,
      formatCreateLabel,
      renderOption,
      renderSelectedValue,
      renderValue,
      testId,
      'data-testid': dataCyProp,
      // className is not part of the API (see packages/ui/README.md) but is
      // still stripped here so a stray one can't clobber the computed classes
      className,
      ...rest
    },
    forwardedRef,
  ) => {
    const [isOpenState, setIsOpen] = useState(false);
    // `inline` is not a dropdown: the list belongs to the page and stays open.
    const isOpen = inline || isOpenState;
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [searchTerm, setSearchTerm] = useState('');
    const onOpenRef = useRef(onOpen);
    onOpenRef.current = onOpen;
    useEffect(() => {
      if (isOpen) onOpenRef.current?.();
    }, [isOpen]);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    useDismissOnOutsidePointerDown(
      !inline && isOpen,
      () => {
        setIsOpen(false);
        setFocusedIndex(-1);
      },
      [triggerRef, contentRef],
    );

    const handleValueChange = onValueChange ?? setValue ?? (() => {});
    const dataCy = dataCyProp ?? testId;
    const sizeKey = size === 'default' ? 'md' : size;
    const renderSelected = renderValue ?? renderSelectedValue;

    const selectedValues: KoobSelectValue[] = Array.isArray(value)
      ? value
      : value != null && value !== ''
        ? [value]
        : [];
    const currentState =
      error || hasError ? 'error' : disabled ? 'disabled' : state;

    // Filter options by search term (supporting both string labels and object items).
    // Same matching as the experiences list and the Additional services modal:
    // accent-insensitive and order-tolerant, so "croisiere" finds "Croisière"
    // and "tour ayutthaya" finds "Ayutthaya day tour".
    // Skipped under `onSearchChange`: the caller already filtered the options
    // (server-side for the async selects), and re-filtering here silently undid
    // that matching whenever it was smarter than a raw substring test.
    const searchWords = onSearchChange ? [] : toSearchWords(searchTerm);
    const filteredOptions = searchWords.length
      ? options.filter((option) => {
          const labelText =
            typeof option.label === 'string' || typeof option.label === 'number'
              ? String(option.label)
              : String(
                  (option.item as any)?.title ||
                    (option.item as any)?.label ||
                    option.value ||
                    '',
                );
          return matchesSearchWords(searchWords, [labelText], option.value);
        })
      : options;

    const createValue = searchTerm.trim();
    const showCreateOption =
      creatable &&
      createValue.length > 0 &&
      !options.some(
        (option) => option.label.toLowerCase() === createValue.toLowerCase(),
      );

    // Reset search term when dropdown closes
    useEffect(() => {
      if (!isOpen) {
        setSearchTerm('');
        onSearchChange?.('');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Popover does this via onOpenAutoFocus; the inline panel has no such hook.
    useEffect(() => {
      if (inline && isOpen && searchable) searchInputRef.current?.focus();
    }, [inline, isOpen, searchable]);

    const handleSelect = (optionValue: KoobSelectValue) => {
      if (multiple) {
        if (selectedValues.includes(optionValue)) {
          handleValueChange(selectedValues.filter((v) => v !== optionValue));
        } else {
          handleValueChange([...selectedValues, optionValue]);
        }
        // Drops the highlight so a second Enter belongs to the host (the trip
        // wizard advances a step) instead of toggling the same option back off.
        setFocusedIndex(-1);
      } else {
        handleValueChange(optionValue);
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (disabled) return;

      switch (e.key) {
        // Keys the select actually consumes stop here, so a host binding the
        // same key globally (the trip wizard's Enter) only sees the leftovers.
        case 'Enter':
        case ' ':
          // Space belongs to the search box when it has focus.
          if (
            e.key === ' ' &&
            document.activeElement === searchInputRef.current
          )
            break;
          if (!isOpen) {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen(true);
            setFocusedIndex(0);
          } else if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
            e.preventDefault();
            e.stopPropagation();
            const option = filteredOptions[focusedIndex];
            if (!option.disabled) handleSelect(option.value);
          }
          // Open with nothing highlighted: not our key.
          break;
        // Arrows drive the list from the trigger AND from the search box —
        // typing then picking has to work without touching the mouse.
        case 'ArrowDown':
          e.preventDefault();
          e.stopPropagation();
          if (!isOpen) {
            setIsOpen(true);
            setFocusedIndex(0);
          } else {
            setFocusedIndex((prev) =>
              prev < filteredOptions?.length - 1 ? prev + 1 : 0,
            );
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          e.stopPropagation();
          if (isOpen) {
            setFocusedIndex((prev) =>
              prev > 0 ? prev - 1 : filteredOptions?.length - 1,
            );
          }
          break;
      }
    };

    // Multi-select: one tag per selected value, in selection order. Values
    // with no matching option (creatable) still get a tag from their raw
    // value so they can be removed.
    const selectedTags = multiple
      ? selectedValues.map((selectedValue) => ({
          value: selectedValue,
          option: options.find((option) => option.value === selectedValue),
        }))
      : [];
    const hasTags = selectedTags.length > 0;

    const getDisplayValue = () => {
      if (selectedValues?.length === 0) return placeholder;

      const selectedOption = options.find((option) => option.value === value);
      if (renderSelected && selectedOption) {
        return renderSelected(selectedOption);
      }
      // Creatable selects can hold a free-text value that has no option.
      return selectedOption?.label || (creatable && value) || placeholder;
    };

    const withLabel = (control: React.ReactElement) =>
      label != null ? (
        <KoobInputLabel
          label={label}
          required={required}
          size={sizeKey === 'md' ? 'default' : sizeKey}
          disabled={disabled}
        >
          {control}
        </KoobInputLabel>
      ) : (
        control
      );

    const displayValue = getDisplayValue();

    const trigger = (
      <button
        type="button"
        name={name}
        ref={triggerRef}
        onKeyDown={handleKeyDown}
        disabled={disabled || loading}
        // Popover.Trigger supplies the toggle in the popover path; inline has
        // nothing to toggle — its list is always open.
        className={cn(
          'border-solid',
          selectVariants({
            variant,
            size: hasTags ? (`${sizeKey}Multiline` as const) : sizeKey,
            rounded,
            state: currentState,
          }),
          warning && currentState !== 'error' && inputWarningClasses,
          inherited && inputInheritedClasses,
          // Brand highlight like every other field; inline is permanently
          // open, so highlighting it would just be a stuck focus ring.
          isOpen && !inline && !disabled && 'border-brand ring-2 ring-brand/20',
        )}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        data-testid={`${dataCy}-button`}
      >
        <div className="flex items-center justify-between gap-2">
          {hasTags ? (
            <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
              {selectedTags.map(({ value: tagValue, option }) => {
                const tagLabel = option?.label ?? String(tagValue);
                // JS call sites sometimes put a node in `label`
                const tagText =
                  typeof tagLabel === 'string' || typeof tagLabel === 'number'
                    ? `Remove ${tagLabel}`
                    : 'Remove';
                return (
                  <span
                    key={tagValue}
                    className={tagVariants({ size: sizeKey })}
                    data-testid={`${dataCy}-tag`}
                  >
                    <span className="min-w-0 truncate">
                      {option && renderSelected
                        ? renderSelected(option)
                        : tagLabel}
                    </span>
                    {!disabled && !loading && (
                      // Not a <button>: the trigger already is one.
                      // Click/keys are swallowed so removing a tag never
                      // toggles the popover.
                      <span
                        role="button"
                        tabIndex={0}
                        aria-label={tagText}
                        data-testid={`${dataCy}-tag-remove`}
                        className="-mr-0.5 flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand"
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          handleSelect(tagValue);
                        }}
                        onKeyDown={(event) => {
                          if (
                            event.key === 'Enter' ||
                            event.key === ' ' ||
                            event.key === 'Backspace' ||
                            event.key === 'Delete'
                          ) {
                            event.preventDefault();
                            event.stopPropagation();
                            handleSelect(tagValue);
                          }
                        }}
                      >
                        <i className="fa-light fa-xmark" aria-hidden />
                      </span>
                    )}
                  </span>
                );
              })}
            </span>
          ) : (
            <span
              className={cn(
                'block min-w-0 flex-1 truncate',
                selectedValues?.length === 0
                  ? 'text-gray-500'
                  : 'text-gray-900',
              )}
              // Truncation is invisible to hover otherwise: surface the full
              // label through the native tooltip.
              title={
                typeof displayValue === 'string' ? displayValue : undefined
              }
            >
              {displayValue}
            </span>
          )}
          <span className="flex shrink-0 items-center gap-1">
            {clearable &&
              selectedValues.length > 0 &&
              !disabled &&
              !loading && (
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label="Clear selection"
                  className="flex h-5 w-5 cursor-pointer items-center justify-center rounded text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleValueChange(multiple ? [] : null);
                  }}
                >
                  <i className="fa-regular fa-xmark" />
                </span>
              )}
            {loading ? (
              <KoobSpinner size={20} />
            ) : (
              <ChevronIcon isOpen={isOpen} disabled={disabled} />
            )}
          </span>
        </div>
      </button>
    );

    const panel = (
      <>
        {searchable && (
          <div className="flex shrink-0 items-center gap-2 border-b border-solid border-gray-100 px-3">
            <i
              className="fa-regular fa-magnifying-glass text-sm text-gray-400"
              aria-hidden
            />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                onSearchChange?.(e.target.value);
              }}
              className={cn(
                'w-full border-0 bg-transparent p-0 placeholder:text-gray-400 focus:outline-none focus:ring-0',
                searchInputSizeClasses[sizeKey],
              )}
              // Marks the field as the combobox input: hosts that bind Enter
              // or Escape globally (the trip wizard) skip it.
              role="combobox"
              // Inline lists have no closed state, so Escape is never theirs
              // to eat — the host (wizard) gets it.
              aria-expanded={inline ? undefined : isOpen}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && showCreateOption) {
                  e.stopPropagation();
                  e.preventDefault();
                  handleSelect(createValue);
                  return;
                }
                handleKeyDown(e);
              }}
            />
          </div>
        )}

        <div
          // Caps at 15rem when there is room. On a short viewport the popover
          // caps itself to the space left on its side and this list is the
          // part that gives, so the search box above and the create row below
          // keep their height. `min-h-0` is what lets a flex child shrink.
          className="max-h-60 min-h-0 flex-1 overflow-y-auto p-1"
          data-testid={dataCy}
        >
          {loading ? (
            <div className="flex justify-center py-6">
              <KoobSpinner size={20} />
            </div>
          ) : filteredOptions?.length === 0 && !showCreateOption ? (
            <div className="py-6 text-center text-sm text-gray-500">
              {emptyState ?? 'No options found'}
            </div>
          ) : (
            <ul role="listbox">
              {showCreateOption && (
                <li
                  onClick={() => handleSelect(createValue)}
                  className={optionVariants({
                    state: 'default',
                    size: sizeKey,
                  })}
                  role="option"
                  aria-selected={false}
                  data-testid={`${dataCy}-create-option`}
                >
                  <span className="w-4 shrink-0" aria-hidden />
                  <span className="min-w-0 flex-1 truncate italic text-gray-600">
                    {formatCreateLabel
                      ? formatCreateLabel(createValue)
                      : `"${createValue}"`}
                  </span>
                </li>
              )}
              {filteredOptions?.map((option, index) => {
                const isSelected = selectedValues.includes(option.value);
                const isFocused = index === focusedIndex;

                let optionState:
                  | 'default'
                  | 'focused'
                  | 'selected'
                  | 'selectedFocused'
                  | 'disabled' = 'default';
                if (option.disabled) {
                  optionState = 'disabled';
                } else if (isSelected && isFocused) {
                  optionState = 'selectedFocused';
                } else if (isSelected) {
                  optionState = 'selected';
                } else if (isFocused) optionState = 'focused';

                return (
                  <li
                    key={option.value}
                    onClick={() =>
                      !option.disabled && handleSelect(option.value)
                    }
                    className={optionVariants({
                      state: optionState,
                      size: sizeKey,
                    })}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={option.disabled || undefined}
                    data-testid={option.label}
                  >
                    {/* Same anatomy for single and multiple: a fixed
                              leading slot where the check appears, so rows
                              align and the two modes look identical. */}
                    <span
                      className="flex w-4 shrink-0 items-center justify-center text-orange-600"
                      aria-hidden
                    >
                      {isSelected && <i className={icons.check} />}
                    </span>
                    <span className="min-w-0 flex-1">
                      {renderOption ? (
                        renderOption(option, {
                          selected: isSelected,
                          focused: isFocused,
                        })
                      ) : (
                        <span
                          className="block truncate"
                          title={
                            typeof option.label === 'string'
                              ? option.label
                              : undefined
                          }
                        >
                          {option.label}
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {onCreate && (
          <div className="shrink-0 border-t border-solid border-gray-100 p-1">
            <div
              role="button"
              tabIndex={0}
              className={cn(
                optionVariants({ state: 'default', size: sizeKey }),
                'font-medium',
              )}
              data-testid={`${dataCy}-create-action`}
              onClick={() => {
                setIsOpen(false);
                setFocusedIndex(-1);
                onCreate();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsOpen(false);
                  setFocusedIndex(-1);
                  onCreate();
                }
              }}
            >
              <span
                className="flex w-4 shrink-0 items-center justify-center"
                aria-hidden
              >
                <i className="fa-light fa-circle-plus" />
              </span>
              <span className="min-w-0 flex-1 truncate">
                {createLabel ?? 'Create new'}
              </span>
            </div>
          </div>
        )}
      </>
    );

    if (inline) {
      return withLabel(
        <div
          translate="no"
          className={cn('notranslate w-full', TRIGGER_MIN_WIDTH)}
          ref={forwardedRef}
          {...rest}
        >
          {trigger}
          <div
            ref={contentRef}
            translate="no"
            className={cn('notranslate mt-2', dropdownClasses)}
          >
            {panel}
          </div>
        </div>,
      );
    }

    return withLabel(
      <Popover.Root
        open={isOpen}
        onOpenChange={(open) => {
          if (open && (disabled || loading)) return;
          setIsOpen(open);
          if (!open) setFocusedIndex(-1);
        }}
      >
        <div
          translate="no"
          className={cn('notranslate relative w-full', TRIGGER_MIN_WIDTH)}
          ref={forwardedRef}
          {...rest}
        >
          <Popover.Trigger asChild>{trigger}</Popover.Trigger>

          <Popover.Portal>
            <Popover.Content
              ref={contentRef}
              translate="no"
              align="start"
              side={side}
              sideOffset={8}
              collisionPadding={8}
              // Searchable: focus the search input on open. Otherwise keep
              // focus on the trigger so its keyboard navigation keeps
              // receiving keys.
              onOpenAutoFocus={(e) => {
                e.preventDefault();
                if (searchable) searchInputRef.current?.focus();
              }}
              style={{
                // Never past the fold: the column inside shrinks to fit.
                maxHeight: 'var(--radix-popover-content-available-height)',
                // At least as wide as the trigger, free to grow with its own
                // content (flags + labels stay readable behind a narrow
                // trigger) up to whatever the viewport allows.
                minWidth: 'var(--radix-popover-trigger-width)',
                width: 'max-content',
                maxWidth:
                  'min(var(--radix-popover-content-available-width), 28rem)',
                // The open menu must paint above any modal it was triggered
                // from — see the scale in packages/ui/z-index.js.
                zIndex: zIndex.popover,
              }}
              className={cn('notranslate flex flex-col', dropdownClasses)}
            >
              {panel}
            </Popover.Content>
          </Popover.Portal>
        </div>
      </Popover.Root>,
    );
  },
);

KoobInputSelect.displayName = 'KoobInputSelect';

function ChevronIcon({
  isOpen,
  disabled,
}: {
  isOpen: boolean;
  disabled: boolean;
}) {
  return (
    <svg
      className={cn(
        'w-5 h-5 transition-transform duration-200',
        disabled ? 'text-gray-300' : 'text-gray-400',
        isOpen && 'rotate-180',
      )}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );
}

export default KoobInputSelect;
